import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { VegaLite } from 'react-vega';
import { useNumberPopover } from '../Ladder/NumberPopover';
import { bundleClaim, type LadderAnswer } from '../Ladder/claims';
import { vegaLiteConfig } from '../../design/charts';
import { useStromaTokens } from '../../design/ThemeProvider';

interface Props {
  bundle: any;
  ladder?: LadderAnswer | null;
}

export default function ChartBundle({ bundle, ladder }: Props) {
  // the page's own tokens: the simplified app's look
  const { tokens, typeface } = useStromaTokens();
  const { open } = useNumberPopover();
  // A chart point is an object too (#183): a click finds the claim whose
  // row matches the datum — in the table THIS bundle was drawn from — and
  // opens the number popover where the pointer is.
  const onNewView = React.useCallback((view: any) => {
    if (!ladder) return;
    view.addEventListener('click', (event: any, item: any) => {
      const datum = item?.datum;
      if (!datum || typeof datum !== 'object') return;
      const claim = bundleClaim(ladder, bundle, datum);
      if (claim) open(claim, null, { left: event.clientX, top: event.clientY });
    });
  }, [ladder, bundle, open]);
  const viz = bundle?.visualization || {};
  const spec = viz.vega_lite;
  const chartType = viz.chart_type;
  const kpi = viz.kpi;

  const patchedSpec = React.useMemo(() => {
    if (!spec) return spec;
    const config = vegaLiteConfig(tokens, typeface);
    const themed = { ...spec, config: { ...config, ...(spec.config || {}) } };
    if (spec.width === 'container' || spec.width == null) {
      return { ...themed, width: 640, autosize: { type: 'fit', contains: 'padding' } };
    }
    return themed;
  }, [spec, tokens, typeface]);

  if (chartType === 'kpi' && kpi) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
        <Typography variant="h4" className="stroma-number" sx={{ fontWeight: 700 }}>{kpi.value}</Typography>
      </Box>
    );
  }

  if (!spec) return null;

  return (
    <Box sx={{ py: 2, width: '100%', overflow: 'auto' }}>
      <VegaLite spec={patchedSpec} actions={false} onNewView={onNewView} />
    </Box>
  );
}
